/**
 * 等级晋级 — Check user upgrade eligibility and execute promotions.
 *
 * GET  /api/admin/level-upgrade/check/:userId
 * POST /api/admin/level-upgrade/promote/:userId
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Row,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, CrownOutlined } from '@ant-design/icons';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface RequirementResult {
  name: string;
  required: string;
  actual: string;
  met: boolean;
}

interface UpgradeCheck {
  level_code: string;
  level_name: string;
  passed: boolean;
  requirements: RequirementResult[];
}

interface UpgradeCheckResult {
  current_level: string;
  eligible_level: string | null;
  checks: UpgradeCheck[];
}

interface PromoteResult {
  old_level: string;
  new_level: string;
  rp_awarded: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LevelUpgradePage() {
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('platform.level.edit');
  const [userId, setUserId] = useState<number | null>(null);
  const [checkResult, setCheckResult] = useState<UpgradeCheckResult | null>(null);
  const [promoteResult, setPromoteResult] = useState<PromoteResult | null>(null);

  const checkMut = useMutation({
    mutationFn: (uid: number) =>
      api.get(`/api/admin/level-upgrade/check/${uid}`) as Promise<any>,
    onSuccess: (data: UpgradeCheckResult) => {
      setCheckResult(data);
      setPromoteResult(null);
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : '查询失败'),
  });

  const promoteMut = useMutation({
    mutationFn: (params: { userId: number; target_level: string }) =>
      api.post(`/api/admin/level-upgrade/promote/${params.userId}`, {
        target_level: params.target_level,
      }) as Promise<any>,
    onSuccess: (data: PromoteResult) => {
      setPromoteResult(data);
      setCheckResult(null);
      message.success(`晋级成功: ${data.old_level} → ${data.new_level}`);
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : '晋级失败'),
  });

  const handleCheck = () => {
    if (!userId) return;
    checkMut.mutate(userId);
  };

  const handlePromote = (targetLevel: string) => {
    if (!userId) return;
    Modal.confirm({
      title: '确认晋级',
      content: `确定将用户 #${userId} 晋级到 ${targetLevel} 吗？`,
      onOk: () => promoteMut.mutate({ userId, target_level: targetLevel }),
    });
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>等级晋级检查</h2>

      {/* Search bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form layout="inline" onFinish={handleCheck}>
          <Form.Item label="用户ID">
            <InputNumber
              min={1}
              style={{ width: 160 }}
              value={userId}
              onChange={(v) => setUserId(v)}
              placeholder="输入用户ID"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={checkMut.isPending}
              disabled={!userId}
            >
              检查晋级资格
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Results */}
      {promoteResult && (
        <Result
          status="success"
          icon={<CrownOutlined />}
          title="晋级成功"
          subTitle={
            <Descriptions column={3}>
              <Descriptions.Item label="原等级">{promoteResult.old_level}</Descriptions.Item>
              <Descriptions.Item label="新等级">{promoteResult.new_level}</Descriptions.Item>
              <Descriptions.Item label="晋级奖RP">{promoteResult.rp_awarded}</Descriptions.Item>
            </Descriptions>
          }
        />
      )}

      {checkResult && (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="当前等级">
                    <Tag>{checkResult.current_level}</Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={16}>
                {checkResult.eligible_level ? (
                  <Alert
                    type="success"
                    showIcon
                    message={`可晋级到: ${checkResult.eligible_level}`}
                    action={
                      canEdit ? (
                        <Button
                          type="primary"
                          size="small"
                          loading={promoteMut.isPending}
                          onClick={() => handlePromote(checkResult.eligible_level!)}
                        >
                          执行晋级
                        </Button>
                      ) : null
                    }
                  />
                ) : (
                  <Alert type="info" showIcon message="暂不满足更高等级的晋级条件" />
                )}
              </Col>
            </Row>
          </Card>

          {/* Detail checks per level */}
          {checkResult.checks.map((chk) => (
            <Card
              key={chk.level_code}
              size="small"
              title={
                <Space>
                  {chk.passed ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  )}
                  {chk.level_code} {chk.level_name}
                  {chk.passed && <Tag color="green">通过</Tag>}
                </Space>
              }
              style={{ marginBottom: 8 }}
            >
              <Table
                size="small"
                bordered
                rowKey="name"
                dataSource={chk.requirements}
                pagination={false}
                columns={[
                  { title: '条件', dataIndex: 'name', width: 200 },
                  { title: '要求', dataIndex: 'required', width: 150 },
                  { title: '实际', dataIndex: 'actual', width: 150 },
                  {
                    title: '结果',
                    dataIndex: 'met',
                    width: 80,
                    render: (v: boolean) =>
                      v ? (
                        <Tag color="green">达标</Tag>
                      ) : (
                        <Tag color="red">未达标</Tag>
                      ),
                  },
                ]}
              />
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
